import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import AppBreadCrumb from '../../common/AppBreadCrumb';
import AppToaster from '../../AppToaster';
import { createStockConversion, fetchStockConversion, updateStockConversion } from '../../../api/StockConversionsAPI';
import { getProducts } from '../../../api/ProductsAPI';
import { handleApiError } from '../../../api/risapi';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from 'date-fns';
import { useNavigate, useParams } from 'react-router-dom';
import { capitalizeFirstLetter } from '../../../commonfunctions';

const StockConversionForm = ({ formType, currentConversionId }) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        product_id: null,
        from_uom_id: null,
        quantity_converted: '',
        conversion_factor: 1,
        base_quantity_generated: 0,
        conversion_date: new Date(),
        narration: '',
    });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    
    const [showToaster, setShowToaster] = useState(false);
    const [toaster, setToaster] = useState({});
    
    const [loadingDropdowns, setLoadingDropdowns] = useState(true);
    const [loadingEditData, setLoadingEditData] = useState(false);
    const [productOptions, setProductOptions] = useState([]);
    const [uomOptions, setUomOptions] = useState([]); // UOMs for the selected product

    // --- Fetch Products Dropdown ---
    useEffect(() => {
        document.title = `${capitalizeFirstLetter(formType)} Stock Conversion`;
        setLoadingDropdowns(true);
        getProducts({ paginate: false, with: ['productUnits.uom', 'baseUom'] }, (response) => {
            setLoadingDropdowns(false);
            if (response.products) {
                const productsData = Array.isArray(response.products) ? response.products : response.products.data;
                setProductOptions(productsData.map(p => ({
                    value: p.id,
                    label: `${p.name} ${p.code ? `(${p.code})` : ''}`,
                    productData: p 
                })));
            } else {
                handleApiError(response);
                setToaster({ type: 'danger', heading: 'Error', message: 'Could not load products.' });
                setShowToaster(true);
            }
        });
    }, []); // Runs once on mount

    // --- Fetch Edit Data ---
  useEffect(() => {
        if (formType === 'edit' && currentConversionId && productOptions.length > 0) {
            setLoadingEditData(true);
            fetchStockConversion(currentConversionId, (response) => {
                
                setLoadingEditData(false);
                if (response.stock_conversion) {
                    const fetched = response.stock_conversion;
                    
                    // --- FOOLPROOF DATE PARSING ---
                    let conversionDate = new Date(); // Fallback to today
                    if (fetched.conversion_date) {
                        // Use native JS date parsing
                        const parsedDate = new Date(fetched.conversion_date);
                        
                        // Check if it's an actual, valid Date object by getting its time
                        if (!isNaN(parsedDate.getTime())) {
                            conversionDate = parsedDate;
                        } else {
                            console.warn("Invalid date received from API, falling back to today.");
                        }
                    }
                    
                    // Manually trigger product change to populate UOMs
                    const selectedProduct = productOptions.find(p => p.value === fetched.product_id);
                    if (selectedProduct) {
                        handleProductChange(selectedProduct, fetched.from_uom_id); 
                    }

                    setFormData({
                        product_id: fetched.product_id,
                        from_uom_id: fetched.from_uom_id,
                        quantity_converted: fetched.quantity_converted || '',
                        conversion_factor: fetched.conversion_factor || 1,
                        base_quantity_generated: fetched.base_quantity_generated || 0,
                        conversion_date: conversionDate, // Guaranteed to be a valid Date object now
                        narration: fetched.narration || '',
                    });
                } else {
                    handleApiError(response);
                    setToaster({ type: 'danger', heading: 'Error', message: 'Could not load conversion data.' });
                    setShowToaster(true);
                }
            });
        }
    }, [formType, currentConversionId, productOptions]);

    // --- Handlers ---
    const handleProductChange = (selectedOption, uomToSelect = null) => {
        const productData = selectedOption ? selectedOption.productData : null;
        let newUomOptions = [];
        
        if (productData && Array.isArray(productData.product_units)) {
            newUomOptions = productData.product_units.map(unit => ({
                value: unit.uom_id,
                label: unit.uom ? `${unit.uom.name} (${unit.uom.short_name})` : 'Unknown Unit',
                conversion_factor: unit.conversion_factor
            }));
        }

        setUomOptions(newUomOptions);
        
        // Find the UOM to pre-select (for edit mode)
        const selectedUom = uomToSelect ? newUomOptions.find(u => u.value === uomToSelect) : null;

        setFormData(prev => ({
            ...prev,
            product_id: selectedOption ? selectedOption.value : null,
            from_uom_id: selectedUom ? selectedUom.value : null,
            conversion_factor: selectedUom ? selectedUom.conversion_factor : 1,
            base_quantity_generated: 0,
            quantity_converted: '', // Reset quantity
        }));
        setErrors(prev => ({ ...prev, product_id: null, from_uom_id: null }));
    };

    const handleUomChange = (selectedOption) => {
        const factor = selectedOption ? selectedOption.conversion_factor : 1;
        const qty = parseFloat(formData.quantity_converted) || 0;
        
        setFormData(prev => ({
            ...prev,
            from_uom_id: selectedOption ? selectedOption.value : null,
            conversion_factor: factor,
            base_quantity_generated: (qty * factor).toFixed(2)
        }));
        setErrors(prev => ({ ...prev, from_uom_id: null }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'quantity_converted') {
            const qty = parseFloat(value) || 0;
            const factor = parseFloat(formData.conversion_factor) || 1;
            setFormData(prev => ({
                ...prev,
                quantity_converted: value,
                base_quantity_generated: (qty * factor).toFixed(2)
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
        setErrors(prev => ({ ...prev, [name]: null }));
    };

    const handleDateChange = (date) => {
        setFormData(prev => ({ ...prev, conversion_date: date }));
        setErrors(prev => ({ ...prev, conversion_date: null }));
    };

    const handleSave = () => {
        setErrors({});
        setSaving(true);
        const apiCall = formType === 'create' ? createStockConversion : updateStockConversion;

        const dataToSend = {
            ...formData,
            conversion_date: format(formData.conversion_date, 'yyyy-MM-dd'),
        };
        if(formType === 'edit') dataToSend.id = currentConversionId;
        delete dataToSend.base_quantity_generated; // Not needed by backend

        apiCall(dataToSend, (response) => {
            setSaving(false);
            if (response.stock_conversion) {
                const message = `Stock Conversion ${formType === 'create' ? 'created' : 'updated'} successfully.`;
                navigate('/franchisee_user/inventory/stock-conversions', { state: { message } });
            } else {
                const errorMsg = response.error?.response?.data?.message || 'Could not save. Check errors.';
                handleApiError(response, setErrors);
                setToaster({ type: 'danger', heading: 'Error', message: errorMsg });
                setShowToaster(true);
            }
        });
    };

    const isLoading = loadingDropdowns || loadingEditData;
    const pageTitle = capitalizeFirstLetter(formType) + ' Stock Conversion';

    return (
        <>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <main>
                <div className="container-fluid">
                    <AppBreadCrumb title={pageTitle}>
                        <li><a className="f-s-14 f-w-500" href="#">Inventory</a></li>
                        <li><a className="f-s-14 f-w-500" href="/franchisee_user/inventory/stock-conversions">Stock Loosening</a></li>
                        <li className="active"><a className="f-s-14 f-w-500" href="#">{pageTitle}</a></li>
                    </AppBreadCrumb>

                    {isLoading && <div className="loader_box"><div className="loader_14"></div></div>}

                    {!isLoading && (
                        <div className="row">
                            <div className="col-lg-8 col-md-10 mx-auto">
                                <div className="card">
                                    <div className="card-header">
                                        <h5 className="mb-0">{pageTitle}</h5>
                                    </div>
                                    <div className="card-body">
                                        <form className="app-form" onSubmit={(e) => {e.preventDefault(); handleSave();}}>
                                            <div className="row">
                                                {/* Conversion Date */}
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Conversion Date *</label>
                                                    <DatePicker selected={formData.conversion_date} onChange={handleDateChange} dateFormat="yyyy-MM-dd" className="form-control" wrapperClassName="d-block" />
                                                    <span className="text-danger small">{errors?.conversion_date}</span>
                                                </div>

                                                {/* Product */}
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Product *</label>
                                                    <Select
                                                        options={productOptions}
                                                        value={productOptions.find(o => o.value === formData.product_id)}
                                                        onChange={handleProductChange}
                                                        placeholder="Select Product" isClearable
                                                        isDisabled={formType === 'edit'} // Don't allow product change on edit
                                                    />
                                                    <span className="text-danger small">{errors?.product_id}</span>
                                                </div>

                                                {/* From UOM */}
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Loosen From Unit *</label>
                                                    <Select
                                                        options={uomOptions}
                                                        value={uomOptions.find(o => o.value === formData.from_uom_id)}
                                                        onChange={handleUomChange}
                                                        placeholder="Select unit to loosen" isClearable
                                                        isDisabled={!formData.product_id || uomOptions.length === 0}
                                                    />
                                                    <span className="text-danger small">{errors?.from_uom_id}</span>
                                                    {!formData.product_id && <small className="text-muted">Select a product to see units.</small>}
                                                    {formData.product_id && uomOptions.length === 0 && <small className="text-danger">This product has no alternate units defined.</small>}
                                                </div>

                                                {/* Quantity to Convert */}
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Quantity to Loosen *</label>
                                                    <input
                                                        type="number" step="0.01" min="0.01"
                                                        className="form-control text-end"
                                                        name="quantity_converted"
                                                        value={formData.quantity_converted}
                                                        onChange={handleInputChange}
                                                        disabled={!formData.from_uom_id}
                                                    />
                                                    <span className="text-danger small">{errors?.quantity_converted}</span>
                                                </div>

                                                {/* Generated Quantity (Read-only) */}
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Base Units Generated</label>
                                                    <input
                                                        type="text"
                                                        className="form-control text-end"
                                                        value={formData.base_quantity_generated}
                                                        readOnly disabled
                                                    />
                                                </div>
                                                
                                                {/* Narration */}
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Narration</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="narration"
                                                        value={formData.narration}
                                                        onChange={handleInputChange}
                                                    />
                                                    <span className="text-danger small">{errors?.narration}</span>
                                                </div>
                                                
                                                {/* Hidden conversion factor */}
                                                <input type="hidden" name="conversion_factor" value={formData.conversion_factor} />

                                                {/* Action Buttons */}
                                                <div className="col-12 mt-3">
                                                    <div className="text-end border-top pt-3">
                                                        <button className="btn btn-danger btn-sm me-2" type="button" onClick={() => navigate('/franchisee_user/inventory/stock-conversions')} disabled={saving}>
                                                            Cancel
                                                        </button>
                                                        <button className="btn btn-primary" type="submit" disabled={saving || uomOptions.length === 0}>
                                                            {saving ? 'Processing...' : (formType === 'create' ? 'Process Loosening' : 'Update Conversion')}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </>
    );
};

export default StockConversionForm;